<template>
  <div class="item">
    <!--
      Изображение позиции.

      У комбо картинку собираем из КУПЛЕННЫХ позиций — так гость узнаёт в истории
      свой набор, а не видит общую картинку набора (состав комбо с тех пор могли
      поменять). Раскладки нет — показываем обычную картинку.
    -->
    <div v-if="comboSceneLayers.length > 0" class="item-img item-img--scene">
      <ComboScene :layers="comboSceneLayers" />
    </div>
    <img
      v-else-if="itemImage"
      :src="itemImage"
      class="item-img"
      :alt="item.productName || 'Позиция'"
    />
    <div v-else class="item-img item-img--placeholder">
      <UIcon name="i-heroicons-photo" class="h-5 w-5" />
    </div>

    <div class="item-body">
      <div class="item-row">
        <span class="item-name">
          {{ item.productName || 'Позиция' }}
          <UiKitChip
            v-if="isGift"
            color="success"
            variant="flat"
            size="sm"
            data-testid="order-item-gift-badge"
          >
            Подарок
          </UiKitChip>
        </span>
        <!--
          У подарочной строки показываем цену К ОПЛАТЕ: полная цена зачёркнута.
          Скидка учтена ровно здесь и в «Сумме заказа» — отдельной строкой «Подарок»
          она уже не дублируется (см. OrderSummary).
        -->
        <span class="item-total">
          <s v-if="isGift" class="item-total--struck">{{ item.totalPrice }} ₽</s>
          {{ payablePrice }} ₽
        </span>
      </div>

      <!-- Детали цены -->
      <div class="item-meta">
        <span>{{ item.unitPrice }} ₽ × {{ item.quantity }}</span>
        <span v-if="addonsPrice > 0">+ добавки {{ addonsPrice }} ₽</span>
      </div>

      <!-- Состав комбо -->
      <OrderComboComposition :item="item" />

      <!-- Добавки -->
      <div v-if="selectedAddons.length > 0" class="item-addons">
        <span
          v-for="(addon, idx) in selectedAddons"
          :key="addon.id"
        >
          + {{ addon.name }}{{ addon.price > 0 ? ` ${addon.price} ₽` : '' }}{{ idx < selectedAddons.length - 1 ? ', ' : '' }}
        </span>
      </div>

      <!-- Исключения -->
      <div v-if="selectedExcludes.length > 0" class="item-excludes">
        Без:
        <span
          v-for="(exclude, idx) in selectedExcludes"
          :key="exclude.id"
        >
          {{ exclude.name }}{{ idx < selectedExcludes.length - 1 ? ', ' : '' }}
        </span>
      </div>

      <!-- Примечания к позиции -->
      <p v-if="item.notes" class="item-notes">
        Примечание: {{ item.notes }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import OrderComboComposition from './OrderComboComposition.vue'
import { getItemPayablePrice, isGiftItem } from '@entities/order'
import { ComboScene, orderComboSceneLayers } from '@entities/combo'

interface Props {
  item: any
  addons: any[]
  excludes: any[]
}

const props = defineProps<Props>()

/** Позиция отдана в подарок (за сумму заказа или за частоту) */
const isGift = computed(() => isGiftItem(props.item))

/** Цена строки к оплате: бесплатна ровно одна единица подарка */
const payablePrice = computed(() => getItemPayablePrice(props.item))

/**
 * Слои составной картинки комбо: позиции набора на своих местах.
 * Пусто у обычных товаров и у комбо без расставленных слотов.
 */
const comboSceneLayers = computed(() => orderComboSceneLayers(props.item?.comboGroups))

/**
 * Вычисляем изображение товара
 */
const itemImage = computed(() => {
  // Проверяем массив img напрямую в item
  if (props.item.img && Array.isArray(props.item.img) && props.item.img.length > 0) {
    return props.item.img[0].url || props.item.img[0]
  }
  
  // Проверяем вложенный product.img
  if (props.item.product?.img && Array.isArray(props.item.product.img) && props.item.product.img.length > 0) {
    return props.item.product.img[0].url || props.item.product.img[0]
  }
  
  return null
})

/**
 * Вычисляем выбранные добавки по ID из item.addons
 */
const selectedAddons = computed(() => {
  if (!props.item.addons || !Array.isArray(props.item.addons) || props.item.addons.length === 0) {
    return []
  }
  
  if (!props.addons || !Array.isArray(props.addons)) {
    return []
  }
  
  // item.addons - это массив ID (например: [119, 18, 385, 416])
  // Находим полные объекты добавок по этим ID
  return props.item.addons
    .map((addonId: number | string) => {
      return props.addons.find(addon => String(addon.id) === String(addonId))
    })
    .filter(Boolean) // Удаляем undefined значения
})

/**
 * Вычисляем выбранные исключения по ID из item.excludes
 */
const selectedExcludes = computed(() => {
  if (!props.item.excludes || !Array.isArray(props.item.excludes) || props.item.excludes.length === 0) {
    return []
  }
  
  if (!props.excludes || !Array.isArray(props.excludes)) {
    return []
  }
  
  // item.excludes - это массив ID
  // Находим полные объекты исключений по этим ID
  return props.item.excludes
    .map((excludeId: number | string) => {
      return props.excludes.find(exclude => String(exclude.id) === String(excludeId))
    })
    .filter(Boolean) // Удаляем undefined значения
})

/**
 * Вычисляем общую стоимость добавок
 */
const addonsPrice = computed(() => {
  return selectedAddons.value.reduce((sum: number, addon: any) => {
    return sum + (addon.price || 0)
  }, 0)
})
</script>

<style scoped lang="scss">
.item {
  @apply flex gap-3 items-start py-3;

  &-img {
    @apply w-12 h-12 rounded-hui-md object-cover shrink-0;

    &--placeholder {
      @apply flex items-center justify-center bg-hui-content2 text-hui-default-400;
    }

    // Сцене нужна своя коробка: слои позиционируются от неё, а вылезающие за край
    // (соус «внахлёст») режем — иначе они лезут на название позиции
    &--scene {
      @apply overflow-hidden bg-hui-content2;
    }
  }

  &-body {
    @apply flex-1 min-w-0 grid gap-0.5;
  }

  &-row {
    @apply flex justify-between items-start gap-3;
  }

  &-name {
    @apply text-base font-medium text-hui-foreground flex flex-wrap items-center gap-1.5;
  }

  &-total {
    @apply font-semibold text-base text-hui-foreground whitespace-nowrap shrink-0;

    &--struck {
      @apply font-normal text-sm text-hui-default-400 mr-1;
    }
  }

  &-meta {
    @apply flex flex-wrap gap-x-2 text-sm text-hui-default-500;
  }

  &-addons {
    @apply text-xs text-hui-default-500;
  }

  &-excludes {
    @apply text-xs text-hui-warning-600;
  }

  &-notes {
    @apply text-xs text-hui-default-400 italic;
  }
}
</style>